<template>
    <div v-if="compact" class="@container/scale flex flex-col gap-4">
        <div class="flex items-center justify-between gap-4 border-b border-border pb-2">
            <span class="body-small-semibold text-ink">{{ leftLabel }}</span>
            <span class="body-small-semibold text-ink @max-2xs/scale:sr-only">{{ t("connections.pairPerspective.scale.balanced") }}</span>
            <span class="body-small-semibold text-right text-ink">{{ rightLabel }}</span>
        </div>
        <div v-for="person in people" :key="person.id" class="flex flex-col gap-2">
            <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 body-small">
                <span class="body-small-semibold min-w-0 break-words text-ink">{{ person.name }}</span>
                <span class="text-ink-muted">{{ person.score.preference }}</span>
            </div>
            <div
                role="meter"
                :aria-label="t('connections.pairPerspective.scale.memberScale', { name: person.name, left: leftLabel, right: rightLabel })"
                :aria-valuemin="-3"
                :aria-valuemax="3"
                :aria-valuenow="person.score.binned_score"
                :aria-valuetext="person.score.preference"
                class="grid h-2 grid-cols-7 gap-1">
                <span v-for="bin in bins" :key="bin" class="rounded-full" aria-hidden="true" :class="bin === person.score.binned_score ? colorClass : 'bg-surface-sunken'" />
            </div>
        </div>
    </div>
    <div v-else class="flex flex-col gap-4 rounded-lgr bg-surface p-4 fs-mask">
        <div class="grid min-h-16 grid-cols-7 items-end gap-1">
            <div v-for="(bin, index) in bins" :key="bin" class="flex min-w-0 flex-col items-center gap-2">
                <BaseTooltip
                    v-for="person in people.filter((person) => person.score.binned_score === bin)"
                    :key="person.id"
                    :trigger-aria-label="`${person.name} · ${person.score.preference}`"
                    :as-child="false"
                    open-on-click
                    content-class="fs-mask">
                    <template #trigger>
                        <span class="block rounded-full" :class="person.id === 'viewer' ? 'ring-1 ring-ink ring-offset-2 ring-offset-surface' : ''">
                            <PeopleAvatar
                                v-if="person.id === 'viewer'"
                                :initials="person.name"
                                :letters="person.letters"
                                :disabled="!getPerspectiveSwatch(person.letters)"
                                size="sm"
                                aria-hidden="true" />
                            <PerspectiveAvatar v-else :name="person.name" :letters="person.letters" size="sm" aria-hidden="true" />
                        </span>
                    </template>
                    <template #content>{{ `${person.name} · ${person.score.preference}` }}</template>
                </BaseTooltip>
                <span
                    class="mt-2 h-2 w-full"
                    :class="[
                        people.some((person) => person.score.binned_score === bin) ? 'bg-ink-muted' : 'bg-border',
                        { 'rounded-l-full': index === 0, 'rounded-r-full': index === bins.length - 1 },
                    ]"
                    aria-hidden="true" />
            </div>
        </div>
        <div class="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2">
            <span class="scaffold-details-small text-ink md:scaffold-body-small-medium">{{ leftLabel }}</span>
            <span class="details text-center text-ink-muted">{{ t("connections.pairPerspective.scale.balanced") }}</span>
            <span class="scaffold-details-small text-right text-ink md:scaffold-body-small-medium">{{ rightLabel }}</span>
        </div>
        <ul class="sr-only">
            <li v-for="person in people" :key="person.id">
                <span
                    role="meter"
                    :aria-label="t('connections.pairPerspective.scale.memberScale', { name: person.name, left: leftLabel, right: rightLabel })"
                    :aria-valuemin="-3"
                    :aria-valuemax="3"
                    :aria-valuenow="person.score.binned_score"
                    :aria-valuetext="person.score.preference"
                    >{{ `${person.name} · ${person.score.preference}` }}</span
                >
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import type { PairScore } from "~vue/api/generated/pair-perspective";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import { DIMENSION_COLORS } from "~vue/pages/perspectiveTypes";
import { getPerspectiveSwatch } from "~vue/utils/perspectivePalette";
import { getPerspectiveLetters } from "~vue/utils/perspectiveUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    dimension: string;
    viewer: PairScore | null;
    colleague: PairScore | null;
    colleagueName: string;
    leftLabel: string;
    rightLabel: string;
    compact?: boolean;
    viewerArchetype?: string | null;
    colleagueArchetype?: string | null;
}>();
const { t } = useI18n();
const dimensionColors: Readonly<Record<string, string>> = DIMENSION_COLORS;
const colorClass = computed(() => dimensionColors[props.dimension] ?? "bg-ink");
const bins = [-3, -2, -1, 0, 1, 2, 3];
const people = computed(() => [
    ...(props.viewer ? [{ id: "viewer", name: t("connections.pairPerspective.scale.you"), score: props.viewer, letters: getPerspectiveLetters(props.viewerArchetype) }] : []),
    ...(props.colleague ? [{ id: "colleague", name: props.colleagueName, score: props.colleague, letters: getPerspectiveLetters(props.colleagueArchetype) }] : []),
]);
</script>
